import { storeToRefs } from 'pinia'
import { useSettingsStore } from '~/stores/settings'
import { configureGoogleEventSender, configureGoogleAdsPurchaseSender } from '~/composables/useGoogleEvents'
import { configureMetaEventSender } from '~/composables/useFacebookEvents'
import { configureMarketingTracking, updateMarketingConsent, trackMarketingPageView, analyticsContext, updateAnalyticsConsent } from '~/services/marketing.client'

export default defineNuxtPlugin((nuxtApp) => {
    const api = nuxtApp.$api as typeof $fetch
    const { settings } = storeToRefs(useSettingsStore())
    const { consent } = useCookieConsent()
    const analyticsAllowed = computed(() => consent.value.decided && consent.value.analytics)
    const marketingAllowed = computed(() => consent.value.decided && consent.value.marketing)
    const browser = window as Window & { dataLayer: unknown[]; gtag?: (...args: unknown[]) => void }
    browser.dataLayer ??= []
    browser.gtag ??= function (...args) { browser.dataLayer.push(args) }
    browser.gtag('consent', 'default', {
        analytics_storage: 'denied', ad_storage: 'denied', ad_user_data: 'denied', ad_personalization: 'denied',
    })
    let google: ReturnType<typeof useScriptGoogleAnalytics>['proxy'] | undefined
    let meta: ReturnType<typeof useScript<{ fbq: Window['fbq'] }>>['proxy'] | undefined
    let clarity: ReturnType<typeof useScriptClarity>['proxy'] | undefined
    let gtmLoaded = false
    const googleIds = new Set<string>()
    const metaIds = new Set<string>()
    let googlePage = ''
    let metaPage = ''
    let previousPage = ''
    const pageUrl = () => `${location.origin}${location.pathname}`
    const mode = () => settings.value.google_tracking_mode || (settings.value.ga ? 'direct' : settings.value.gtag ? 'gtm' : 'direct')

    configureGoogleEventSender((event, properties) => {
        if (!analyticsAllowed.value) return false
        if (mode() === 'gtm') {
            if (!gtmLoaded) return false
            const { method, search_term, ...ecommerce } = properties
            browser.dataLayer.push({ ecommerce: null })
            browser.dataLayer.push({ event, ...(Array.isArray(properties.items) ? { ecommerce } : properties), ...(method ? { method } : {}), ...(search_term ? { search_term } : {}) })
            return true
        }
        if (!google || !settings.value.ga) return false
        google.gtag('event', event, { ...properties, send_to: settings.value.ga })
        return true
    })
    configureGoogleAdsPurchaseSender(order => {
        const { google_ads_id: id, google_ads_purchase_label: label } = settings.value
        if (!google || !marketingAllowed.value || mode() !== 'direct' || !id || !label) return false
        const purchase = order.tracking?.purchase
        if (order.tracking?.google_ads) google.gtag('set', { user_data: order.tracking.google_ads })
        google.gtag('event', 'conversion', { send_to: `${id}/${label}`,
            transaction_id: order.tracking?.event_id.startsWith('subscription-charge-') ? order.tracking.event_id : String(order.id),
            currency: purchase?.currency ?? order.currency, value: purchase?.value ?? order.total })
        google.gtag('set', { user_data: {} })
        return true
    })
    configureMetaEventSender((command, ...args) => {
        if (!meta || !marketingAllowed.value || !settings.value.facebook_pixel) return false
        if (command === 'track' || command === 'trackCustom') {
            meta.fbq(command === 'track' ? 'trackSingle' : 'trackSingleCustom', settings.value.facebook_pixel, ...args)
            return true
        }
        return false
    })

    configureMarketingTracking({
        settings: () => settings.value,
        allowed: () => marketingAllowed.value,
        analyticsAllowed: () => analyticsAllowed.value,
        send: body => api('marketing/events', { method: 'POST', body, keepalive: true, retry: 0, timeout: 5000 }),
    })

    const pageViews = () => {
        const url = pageUrl()
        const googleKey = `${settings.value.ga || settings.value.gtag}:${url}`
        if (analyticsAllowed.value && googlePage !== googleKey) {
            if (useGoogleEvents().event('page_view', { page_location: url, page_referrer: previousPage || document.referrer, page_title: document.title })) googlePage = googleKey
        }
        const metaKey = `${settings.value.facebook_pixel}:${url}`
        if (marketingAllowed.value && settings.value.facebook_pixel && meta && metaPage !== metaKey) {
            meta.fbq('trackSingle', settings.value.facebook_pixel!, 'PageView')
            metaPage = metaKey
        }
        trackMarketingPageView()
        previousPage = url
    }

    watch([analyticsAllowed, marketingAllowed, () => settings.value], () => nuxtApp.runWithContext(() => {
        updateAnalyticsConsent()
        const analytics = analyticsAllowed.value
        const marketing = marketingAllowed.value
        browser.gtag!('consent', 'update', {
            analytics_storage: analytics ? 'granted' : 'denied',
            ad_storage: marketing ? 'granted' : 'denied',
            ad_user_data: marketing ? 'granted' : 'denied',
            ad_personalization: marketing ? 'granted' : 'denied',
        })
        for (const id of googleIds) (browser as unknown as Record<string, unknown>)[`ga-disable-${id}`] = id.startsWith('G-') ? !analytics || id !== settings.value.ga || mode() !== 'direct' : !marketing || id !== settings.value.google_ads_id
        if (!analytics) googlePage = ''
        if (!marketing) metaPage = ''
        // Load only after the current choice; retrieving a Nuxt registry without a trigger can bypass a pending trigger.
        const googleId = mode() === 'direct' ? (analytics ? settings.value.ga : undefined) || (marketing ? settings.value.google_ads_id : undefined) : undefined
        if (googleId && !googleIds.has(googleId)) {
            if (!google) {
                google = useScriptGoogleAnalytics({ id: googleId, onBeforeGtagStart: gtag => {
                    gtag('set', { send_page_view: false, client_id: analyticsContext()?.client_id })
                } }).proxy
            } else google.gtag('config', googleId, { send_page_view: false, client_id: analyticsContext()?.client_id })
            googleIds.add(googleId)
        }
        const adsId = settings.value.google_ads_id
        if (google && adsId && marketing && mode() === 'direct' && !googleIds.has(adsId)) {
            google.gtag('config', adsId, { send_page_view: false })
            googleIds.add(adsId)
        }
        if (!marketing) google?.gtag('set', { user_data: {} })
        // Existing sites may use GA and GTM together. Only an explicit mode changes that setup.
        if (settings.value.gtag && settings.value.google_tracking_mode !== 'direct' && analytics && !gtmLoaded) {
            browser.gtag!('set', { client_id: analyticsContext()?.client_id })
            useScriptGoogleTagManager({ id: settings.value.gtag })
            gtmLoaded = true
        }
        const metaId = settings.value.facebook_pixel
        if (metaId && marketing && !metaIds.has(metaId)) {
            if (!meta) {
                // Own the bootstrap so autoConfig is disabled before init and the first PageView.
                meta = useScript<{ fbq: Window['fbq'] }>({
                    src: 'https://connect.facebook.net/en_US/fbevents.js', crossorigin: false,
                }, {
                    beforeInit() {
                        if (window.fbq) return
                        const fbq = Object.assign((...args: unknown[]) => {
                            if (fbq.callMethod) fbq.callMethod(...args)
                            else fbq.queue.push(args)
                        }, {
                            queue: [] as unknown[][], loaded: true, version: '2.0',
                            callMethod: undefined as ((...args: unknown[]) => void) | undefined,
                        })
                        window.fbq = Object.assign(fbq, { push: fbq })
                        window._fbq ??= window.fbq
                    },
                    use: () => ({ fbq: window.fbq }),
                }).proxy
            }
            // Events and their currency are supplied by the storefront, not scraped from page text.
            meta.fbq('set', 'autoConfig', false, metaId)
            meta.fbq('init', metaId)
            metaIds.add(metaId)
        }
        meta?.fbq('consent', marketing && !!metaId ? 'grant' : 'revoke')
        if (analytics && settings.value.ms_clarity && settings.value.ms_clarity_id && !clarity) {
            clarity = useScriptClarity({ id: settings.value.ms_clarity_id }).proxy
        }
        clarity?.clarity('consentv2', { analytics_Storage: analytics && !!settings.value.ms_clarity && !!settings.value.ms_clarity_id ? 'granted' : 'denied', ad_Storage: marketing ? 'granted' : 'denied' })
        updateMarketingConsent()
        pageViews()
    }), { immediate: true, deep: true })
    nuxtApp.hook('page:finish', pageViews)
})
